import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect } from 'react';
import { fetchAPI, postAPI } from '../../utils/utils';
import CreateUser from '../gestio/CreateUser';
import './OnboardingWizard.css';
import { getCastellerNameWithMote } from '../../utils/castellerName';
import { getCalendarUpdateErrorMessage } from '../calendar/calendarVisibility';

const ONBOARDING_SKIPPED_KEY = 'aleta_onboarding_skipped';

function OnboardingWizard({ socket, onComplete, onboardingStatus, refreshStatus, userInfo }) {
    useSocketReconnect(socket, () => {
        socket.emit('.request_users');
    });

    const [currentStep, setCurrentStep] = useState(() => {
        // Initialize to the first incomplete step
        if (!onboardingStatus) return 0;
        if (!onboardingStatus.calendar_configured) return 0;
        if (onboardingStatus.castellers_count < 5) return 1;
        if (onboardingStatus.etiquetes_count <= 0) return 2;
        return 0;
    });
    const [calendar, setCalendar] = useState({ ids: [], calendar_targets: {} });
    const [newCalendarId, setNewCalendarId] = useState('');
    const [calendarError, setCalendarError] = useState(null);
    const [castellers, setCastellers] = useState([]);
    const [etiquetes, setEtiquetes] = useState([]);
    
    // Popup state for creating castellers
    const [createUserClosed, setCreateUserClosed] = useState(true);
    
    // Form state for creating etiquetes
    const [newEtiquetaNom, setNewEtiquetaNom] = useState('');

    const steps = [
        { 
            id: 'calendar', 
            title: 'Configurar el calendari', 
            icon: '1',
            complete: onboardingStatus?.calendar_configured 
        },
        { 
            id: 'castellers', 
            title: 'Afegir els castellers', 
            icon: '2',
            complete: onboardingStatus?.castellers_count >= 5 
        },
        { 
            id: 'etiquetes', 
            title: 'Afegir etiquetes', 
            icon: '3',
            complete: onboardingStatus?.etiquetes_count > 0 
        },
    ];

    useEffect(() => {
        fetchAPI('/calendar_keys', setCalendar);
        fetchAPI('/etiquetes', setEtiquetes);
        
        socket.emit('.request_users');
        const onUsers = (users) => {
            setCastellers(users.filter(u => !u.hidden));
        };
        socket.on('.users', onUsers);

        return () => {
            socket.off('.users', onUsers);
        };
    }, [socket]);

    const handleSkip = () => {
        localStorage.setItem(ONBOARDING_SKIPPED_KEY, 'true');
        onComplete();
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onComplete();
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Calendar functions
    const handleCalendarError = error => {
        setCalendarError(getCalendarUpdateErrorMessage(error));
    };

    const addCalendarId = () => {
        if (!newCalendarId.trim()) {
            alert('Introdueix un ID de calendari vàlid');
            return;
        }
        
        const updatedCalendar = {
            ...calendar,
            ids: [...calendar.ids, newCalendarId.trim()]
        };
        
        setCalendarError(null);
        postAPI(
            '/update_calendar_keys',
            { calendar: updatedCalendar },
            savedCalendar => {
                setCalendar(savedCalendar);
                setNewCalendarId('');
                refreshStatus();
            },
            handleCalendarError
        );
    };

    const removeCalendarId = (index) => {
        const removedCalendarId = calendar.ids[index];
        const updatedCalendarTargets = { ...(calendar.calendar_targets || {}) };
        delete updatedCalendarTargets[removedCalendarId];
        const updatedCalendar = {
            ...calendar,
            ids: calendar.ids.filter((_, i) => i !== index),
            calendar_targets: updatedCalendarTargets,
        };
        
        setCalendarError(null);
        postAPI(
            '/update_calendar_keys',
            { calendar: updatedCalendar },
            savedCalendar => {
                setCalendar(savedCalendar);
                refreshStatus();
            },
            handleCalendarError
        );
    };

    // Handle CreateUser popup close
    const handleCreateUserClose = (wasCreated) => {
        setCreateUserClosed(true);
        if (!wasCreated) return;
        socket.emit('.request_users');
        refreshStatus();
    };

    // Etiqueta functions
    const createEtiqueta = () => {
        if (!newEtiquetaNom.trim()) {
            alert('Introdueix un nom per l\'etiqueta');
            return;
        }

        postAPI('/create_etiqueta', { nom: newEtiquetaNom.trim() }, () => {
            fetchAPI('/etiquetes', setEtiquetes);
            setNewEtiquetaNom('');
            refreshStatus();
        });
    };

    const deleteEtiqueta = (id) => {
        postAPI('/delete_etiqueta', { id }, () => {
            fetchAPI('/etiquetes', setEtiquetes);
            refreshStatus();
        });
    };

    const renderStepContent = () => {
        const step = steps[currentStep];

        switch (step.id) {
            case 'calendar':
                return (
                    <div className="onboarding-step-content">
                        <div className="step-description">
                            <p>
                                L'Aleta es sincronitza automàticament amb el vostre Google Calendar. 
                                Només cal que afegiu l'ID del calendari i tots els esdeveniments que 
                                creeu o modifiqueu a Google Calendar apareixeran automàticament a l'Aleta.
                            </p>
                        </div>

                        {calendarError ? (
                            <div className="onboarding-error" role="alert">
                                {calendarError}
                            </div>
                        ) : null}

                        {calendar.ids.length > 0 ? (
                            <div className="calendars-list">
                                <h4>Calendaris configurats:</h4>
                                {calendar.ids.map((id, index) => (
                                    <div key={index} className="calendar-item">
                                        <span className="calendar-id">{id}</span>
                                        <button 
                                            className="remove-btn"
                                            onClick={() => removeCalendarId(index)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        <details className="tutorial-section" open>
                            <summary>Com obtenir l'ID del calendari?</summary>
                            <div className="tutorial-steps">
                                <details className="tutorial-create-calendar">
                                    <summary>No tinc calendari - Com crear-ne un</summary>
                                    <div className="tutorial-create-steps">
                                        <div className="tutorial-step">
                                            <div className="tutorial-step-number">A</div>
                                            <div className="tutorial-step-content">
                                                <strong>Crea un nou calendari</strong>
                                                <p>A <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">Google Calendar</a>, fes clic al botó "+" per crear un nou calendari.</p>
                                                <img className="tutorial-image" src={require('../calendar/tutorial/steps/1.png')} alt="Crear calendari" />
                                            </div>
                                        </div>
                                        <div className="tutorial-step">
                                            <div className="tutorial-step-number">B</div>
                                            <div className="tutorial-step-content">
                                                <strong>Posa-li nom</strong>
                                                <p>Dona-li un nom (ex: "Calendari de la Colla") i crea'l.</p>
                                                <img className="tutorial-image" src={require('../calendar/tutorial/steps/2.png')} alt="Nom del calendari" />
                                            </div>
                                        </div>
                                    </div>
                                </details>

                                <div className="tutorial-step">
                                    <div className="tutorial-step-number">1</div>
                                    <div className="tutorial-step-content">
                                        <strong>Obre la configuració del calendari</strong>
                                        <p>A <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">Google Calendar</a>, fes clic als tres punts del calendari i selecciona "Configuració".</p>
                                        <img className="tutorial-image" src={require('../calendar/tutorial/steps/3.png')} alt="Configuració del calendari" />
                                    </div>
                                </div>
                                <div className="tutorial-step">
                                    <div className="tutorial-step-number">2</div>
                                    <div className="tutorial-step-content">
                                        <strong>Comparteix amb l'Aleta</strong>
                                        <p>A "Compartir amb persones concretes", afegeix aquest correu:</p>
                                        <code className="tutorial-email">calendar-key@calendar-sample-370523.iam.gserviceaccount.com</code>
                                        <img className="tutorial-image" src={require('../calendar/tutorial/steps/4.png')} alt="Compartir calendari" />
                                    </div>
                                </div>
                                <div className="tutorial-step">
                                    <div className="tutorial-step-number">3</div>
                                    <div className="tutorial-step-content">
                                        <strong>Copia l'ID del calendari</strong>
                                        <p>A "Integrar el calendari", copia l'ID (acaba en <em>@gmail.com</em> o <em>@group.calendar.google.com</em>).</p>
                                        <img className="tutorial-image" src={require('../calendar/tutorial/steps/5.png')} alt="ID del calendari" />
                                    </div>
                                </div>
                            </div>
                        </details>

                        <div className="add-calendar-form">
                            <h4>Afegir un calendari:</h4>
                            <div className="input-group">
                                <input
                                    type="text"
                                    value={newCalendarId}
                                    onChange={(e) => setNewCalendarId(e.target.value)}
                                    placeholder="ID del calendari (ex: nom@gmail.com)"
                                />
                                <button 
                                    className="add-btn"
                                    onClick={addCalendarId}
                                >
                                    Afegir
                                </button>
                            </div>
                        </div>

                        {calendar.ids.length > 0 ? (
                            <div className="video-tutorial">
                                <h4>Tutorial: Com afegir esdeveniments</h4>
                                <p className="video-description">
                                    Un cop configurat el calendari, podeu crear esdeveniments directament des de Google Calendar. 
                                    Tots els canvis es sincronitzen automàticament amb l'Aleta: assaigs, actuacions i activitats 
                                    apareixeran a l'aplicació sense haver de fer res més.
                                </p>
                                <div className="video-container">
                                    <iframe
                                        width="100%"
                                        height="315"
                                        src="https://www.youtube.com/embed/3iY_GvgmuVc"
                                        title="Tutorial: Afegir esdeveniments amb Google Calendar"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            </div>
                        ) : null}
                    </div>
                );

            case 'castellers':
                return (
                    <div className="onboarding-step-content">
                        <div className="step-description">
                            <p>
                                Afegeix els membres de la colla. Necessites almenys 5 castellers per continuar.
                                Pots afegir-ne més tard des de la secció de Gestió.
                            </p>
                            <div className="progress-indicator">
                                <span className={`count ${castellers.length >= 5 ? 'complete' : ''}`}>
                                    {castellers.length} / 5 castellers
                                </span>
                            </div>
                        </div>

                        <div className="castellers-numbered-list">
                            <ol>
                                {castellers.slice(0, 10).map((c, index) => (
                                    <li key={c.id}>
                                        <span className="casteller-number">{index + 1}.</span>
                                        <span className="casteller-name">
                                            {getCastellerNameWithMote(c)}
                                        </span>
                                        <span className="casteller-labels">
                                            {c.es_tecnica === 2 ? <span className="label label-tecnica-cap">Cap tècnica</span> : null}
                                            {c.es_tecnica === 1 ? <span className="label label-tecnica">Tècnica</span> : null}
                                            {c.es_junta === 2 ? <span className="label label-junta-cap">Cap junta</span> : null}
                                            {c.es_junta === 1 ? <span className="label label-junta">Junta</span> : null}
                                            {c.music ? <span className="label label-music">Músic</span> : null}
                                            {c.canalla ? <span className="label label-canalla">Canalla</span> : null}
                                            {c.novell ? <span className="label label-novell">Novell</span> : null}
                                        </span>
                                    </li>
                                ))}
                                {castellers.length > 10 ? (
                                    <li className="more-item">+{castellers.length - 10} més...</li>
                                ) : null}
                                <li className="add-item">
                                    <button 
                                        className="add-casteller-btn"
                                        onClick={() => setCreateUserClosed(false)}
                                    >
                                        + Afegir un casteller
                                    </button>
                                </li>
                            </ol>
                        </div>

                        <CreateUser 
                            closed={createUserClosed} 
                            setClosed={handleCreateUserClose}
                            socket={socket}
                            userInfo={userInfo}
                        />

                        <div className="alternative-action">
                            <p>O bé, pots importar una llista de castellers des d'un Excel:</p>
                            <a href="/gestio" className="link-btn">
                                Anar a Gestió →
                            </a>
                        </div>
                    </div>
                );

            case 'etiquetes':
                return (
                    <div className="onboarding-step-content">
                        <div className="step-description">
                            <p>
                                Les etiquetes serveixen per agrupar castellers segons la seva posició (per exemple: "Baix", "Lateral", "Mans").
                                Crea almenys una etiqueta per continuar.
                            </p>
                        </div>

                        {etiquetes.length > 0 ? (
                            <div className="etiquetes-list">
                                <h4>Etiquetes creades:</h4>
                                <div className="etiquetes-grid">
                                    {etiquetes.map((e) => (
                                        <div key={e.id} className="etiqueta-chip">
                                            <span>{e.nom}</span>
                                            <button 
                                                className="remove-chip-btn"
                                                onClick={() => deleteEtiqueta(e.id)}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div className="add-etiqueta-form">
                            <h4>Crear una etiqueta:</h4>
                            <div className="input-group">
                                <input
                                    type="text"
                                    value={newEtiquetaNom}
                                    onChange={(e) => setNewEtiquetaNom(e.target.value)}
                                    placeholder="Nom de l'etiqueta (ex: Baix, Lateral, Mans...)"
                                    onKeyDown={(e) => e.key === 'Enter' && createEtiqueta()}
                                />
                                <button 
                                    className="add-btn"
                                    onClick={createEtiqueta}
                                >
                                    Crear
                                </button>
                            </div>
                        </div>

                        <div className="suggested-etiquetes">
                            <p>Suggeriments d'etiquetes habituals:</p>
                            <div className="suggestions">
                                {['Baix', 'Lateral', 'Agulla', 'Crossa', 'Contrafort', 'Mans', 'Vents', 'Tronc'].map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        className="suggestion-btn"
                                        onClick={() => {
                                            if (!etiquetes.some(e => e.nom.toLowerCase() === suggestion.toLowerCase())) {
                                                postAPI('/create_etiqueta', { nom: suggestion }, () => {
                                                    fetchAPI('/etiquetes', setEtiquetes);
                                                    refreshStatus();
                                                });
                                            }
                                        }}
                                        disabled={etiquetes.some(e => e.nom.toLowerCase() === suggestion.toLowerCase())}
                                    >
                                        + {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="onboarding-wizard">
            <div className="onboarding-container">
                <div className="onboarding-header">
                    <h1>Benvingut a l'Aleta</h1>
                    <p>Configura la teva colla en uns senzills passos</p>
                </div>

                <div className="steps-indicator">
                    {steps.map((step, index) => (
                        <div 
                            key={step.id}
                            className={`step-dot ${index === currentStep ? 'active' : ''} ${step.complete ? 'complete' : ''}`}
                            onClick={() => setCurrentStep(index)}
                        >
                            <span className="step-icon">{step.complete ? '✓' : step.icon}</span>
                            <span className="step-title">{step.title}</span>
                        </div>
                    ))}
                </div>

                <div className="step-content">
                    <h2>Pas {currentStep + 1}: {steps[currentStep].title}</h2>
                    {renderStepContent()}
                </div>

                <div className="onboarding-footer">
                    <button 
                        className="skip-btn"
                        onClick={handleSkip}
                    >
                        Saltar per ara
                    </button>

                    <div className="navigation-btns">
                        {currentStep > 0 ? (
                            <button 
                                className="prev-btn"
                                onClick={handlePrevious}
                            >
                                ← Anterior
                            </button>
                        ) : null}
                        
                        <button 
                            className="next-btn"
                            onClick={handleNext}
                        >
                            {currentStep === steps.length - 1 ? 'Completar' : 'Següent →'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function shouldShowOnboarding(onboardingStatus) {
    if (!onboardingStatus) return false;
    if (onboardingStatus.onboarding_complete) return false;
    if (localStorage.getItem(ONBOARDING_SKIPPED_KEY) === 'true') return false;
    return true;
}

export function shouldShowMiniPopup(onboardingStatus) {
    if (!onboardingStatus) return false;
    if (onboardingStatus.onboarding_complete) return false;
    if (localStorage.getItem(ONBOARDING_SKIPPED_KEY) !== 'true') return false;
    return true;
}

export function resetOnboardingSkipped() {
    localStorage.removeItem(ONBOARDING_SKIPPED_KEY);
}

export function OnboardingMiniPopup({ onboardingStatus, onOpenWizard }) {
    const [collapsed, setCollapsed] = useState(false);

    const steps = [
        { 
            title: 'Configurar el calendari', 
            complete: onboardingStatus?.calendar_configured 
        },
        { 
            title: 'Afegir els castellers', 
            complete: onboardingStatus?.castellers_count >= 5 
        },
        { 
            title: 'Afegir etiquetes', 
            complete: onboardingStatus?.etiquetes_count > 0 
        },
    ];

    const completedCount = steps.filter(s => s.complete).length;

    if (collapsed) {
        return (
            <div className="onboarding-mini-popup collapsed" onClick={() => setCollapsed(false)}>
                <span className="mini-popup-count">{completedCount}/3</span>
            </div>
        );
    }

    return (
        <div className="onboarding-mini-popup">
            <div className="mini-popup-header">
                <span>Benvinguts a l'Aleta!</span>
                <button className="collapse-btn open-btn" onClick={onOpenWizard}>Obrir assistent</button>
            </div>
            <div className="mini-popup-steps">
                {steps.map((step, index) => (
                    <div key={index} className="mini-popup-step">
                        <input 
                            type="checkbox" 
                            checked={step.complete} 
                            readOnly 
                        />
                        <span className={step.complete ? 'completed' : ''}>{index + 1}. {step.title}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default OnboardingWizard;
